/**
 * Filter & Sort utilities for New Arrival / On Sale pages.
 * 
 * Data fetching, sorting, and filtering is now handled by the backend API.
 * This file maintains constants, active filter checks, and client-side sorting helpers.
 */

// Stores where 'category' field data is unreliable (product title as category, garbage, etc.)
export const CATEGORY_FILTER_BLOCKLIST = [
  "Azure",
  "Farah Talib Aziz",
  "Ismail Farid Pakistan",
  "Sobia Nazir",
  "Zainab Chottani",
  "Baroque",
  "Iznik",
  "Niamia",
  "Generation",
  "Limelight",
  "Zellbury",
  "GulAhmed",
];

export const MAX_PRICE_DEFAULT = 100000;
export const DEFAULT_PRICE_RANGE = [0, MAX_PRICE_DEFAULT];

/**
 * Sort options enum.
 */
export const SORT_OPTIONS = {
  MOST_RELEVANT: "most_relevant",
  PRICE_LOW_HIGH: "price_low_high",
  PRICE_HIGH_LOW: "price_high_low",
  NAME_AZ: "name_az",
  NAME_ZA: "name_za",
};

export const SORT_LABELS = {
  [SORT_OPTIONS.MOST_RELEVANT]: "Most Relevant",
  [SORT_OPTIONS.PRICE_LOW_HIGH]: "Price: Low to High",
  [SORT_OPTIONS.PRICE_HIGH_LOW]: "Price: High to Low",
  [SORT_OPTIONS.NAME_AZ]: "Name: A → Z",
  [SORT_OPTIONS.NAME_ZA]: "Name: Z → A",
};

/**
 * Sort sizes in a logical order (XS, S, M, L, XL, etc. then numeric, then others).
 * Used when rendering size filter options from backend.
 */
export function sortSizes(sizes) {
  const sizeOrder = {
    "0XXS": 1, "0XS": 2, "XXS": 3, "XS": 4,
    "00S": 5, "S": 6, "SM": 7,
    "00M": 8, "M": 9,
    "00L": 10, "L": 11,
    "0XL": 12, "XL": 13,
    "XXL": 14, "2XL": 14,
    "3XL": 15, "XXXL": 15,
    "4XL": 16, "5XL": 17,
    "FREE-SIZE": 100, "FREE SIZE": 100, "FREESIZE": 100,
    "CUSTOM ORDER": 101,
  };

  return sizes.sort((a, b) => {
    const orderA = sizeOrder[a] ?? 50;
    const orderB = sizeOrder[b] ?? 50;
    if (orderA !== orderB) return orderA - orderB;
    // If same order bucket, try numeric sort
    const numA = parseInt(a, 10);
    const numB = parseInt(b, 10);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.localeCompare(b);
  });
}

/**
 * Check if any filters are actively applied (not just the default state).
 */
export function hasActiveFilters(filters, minPrice = 0, maxPrice = MAX_PRICE_DEFAULT) {
  if (!filters) return false;

  // Check price range
  if (
    filters.priceRange &&
    (filters.priceRange[0] > minPrice || filters.priceRange[1] < maxPrice)
  ) {
    return true;
  }

  // Check categories
  if (filters.categories && filters.categories.length > 0) return true;

  // Check variants (dynamic)
  if (filters.variants) {
    for (const [key, values] of Object.entries(filters.variants)) {
      if (values && values.length > 0) return true;
    }
  }

  return false;
}

/**
 * Count how many active filters are applied.
 */
export function countActiveFilters(filters, minPrice = 0, maxPrice = MAX_PRICE_DEFAULT) {
  if (!filters) return 0;
  let count = 0;

  if (
    filters.priceRange &&
    (filters.priceRange[0] > minPrice || filters.priceRange[1] < maxPrice)
  ) {
    count++;
  }

  count += (filters.categories || []).length;
  
  if (filters.variants) {
    for (const [key, values] of Object.entries(filters.variants)) {
      count += (values || []).length;
    }
  }

  return count;
}
